import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadApiError} from '../zad/errors'
import {
  fetchZadAccountingReport,
  fetchZadCashFlowReport,
  fetchZadCustomerAccountsReport,
  fetchZadDashboardReport,
  fetchZadPurchasesReport,
  fetchZadSalesReport,
  fetchZadStockReport,
  fetchZadSupplierAccountsReport,
} from './service'
import {ZadReportSliceState, ZadReportsState} from './types'

function emptyReportState<T>(): ZadReportSliceState<T> {
  return {
    data: null,
    loading: false,
    initialized: false,
    error: null,
    errorCode: null,
    errorStatus: null,
  }
}

const initialState: ZadReportsState = {
  dashboard: emptyReportState(),
  salesReport: emptyReportState(),
  purchasesReport: emptyReportState(),
  customerAccounts: emptyReportState(),
  supplierAccounts: emptyReportState(),
  stockReport: emptyReportState(),
  cashFlowReport: emptyReportState(),
  accountingReport: emptyReportState(),
}

function applyPending<T>(state: ZadReportSliceState<T>) {
  state.loading = true
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function applyRejected<T>(state: ZadReportSliceState<T>, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.loading = false
  state.initialized = true
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function applyFulfilled<T>(state: ZadReportSliceState<T>, data: T) {
  state.loading = false
  state.initialized = true
  state.error = null
  state.errorCode = null
  state.errorStatus = null
  state.data = data
}

const zadReportsSlice = createSlice({
  name: 'zadReports',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchZadDashboardReport.pending, (state) => applyPending(state.dashboard))
    builder.addCase(fetchZadDashboardReport.rejected, (state, action) =>
      applyRejected(state.dashboard, action)
    )
    builder.addCase(fetchZadDashboardReport.fulfilled, (state, action) =>
      applyFulfilled(state.dashboard, action.payload.report)
    )

    builder.addCase(fetchZadSalesReport.pending, (state) => applyPending(state.salesReport))
    builder.addCase(fetchZadSalesReport.rejected, (state, action) =>
      applyRejected(state.salesReport, action)
    )
    builder.addCase(fetchZadSalesReport.fulfilled, (state, action) =>
      applyFulfilled(state.salesReport, action.payload.report)
    )

    builder.addCase(fetchZadPurchasesReport.pending, (state) => applyPending(state.purchasesReport))
    builder.addCase(fetchZadPurchasesReport.rejected, (state, action) =>
      applyRejected(state.purchasesReport, action)
    )
    builder.addCase(fetchZadPurchasesReport.fulfilled, (state, action) =>
      applyFulfilled(state.purchasesReport, action.payload.report)
    )

    builder.addCase(fetchZadCustomerAccountsReport.pending, (state) =>
      applyPending(state.customerAccounts)
    )
    builder.addCase(fetchZadCustomerAccountsReport.rejected, (state, action) =>
      applyRejected(state.customerAccounts, action)
    )
    builder.addCase(fetchZadCustomerAccountsReport.fulfilled, (state, action) =>
      applyFulfilled(state.customerAccounts, action.payload.report)
    )

    builder.addCase(fetchZadSupplierAccountsReport.pending, (state) =>
      applyPending(state.supplierAccounts)
    )
    builder.addCase(fetchZadSupplierAccountsReport.rejected, (state, action) =>
      applyRejected(state.supplierAccounts, action)
    )
    builder.addCase(fetchZadSupplierAccountsReport.fulfilled, (state, action) =>
      applyFulfilled(state.supplierAccounts, action.payload.report)
    )

    builder.addCase(fetchZadStockReport.pending, (state) => applyPending(state.stockReport))
    builder.addCase(fetchZadStockReport.rejected, (state, action) =>
      applyRejected(state.stockReport, action)
    )
    builder.addCase(fetchZadStockReport.fulfilled, (state, action) =>
      applyFulfilled(state.stockReport, action.payload.report)
    )

    builder.addCase(fetchZadCashFlowReport.pending, (state) => applyPending(state.cashFlowReport))
    builder.addCase(fetchZadCashFlowReport.rejected, (state, action) =>
      applyRejected(state.cashFlowReport, action)
    )
    builder.addCase(fetchZadCashFlowReport.fulfilled, (state, action) =>
      applyFulfilled(state.cashFlowReport, action.payload.report)
    )

    builder.addCase(fetchZadAccountingReport.pending, (state) => {
      applyPending(state.accountingReport)
      state.accountingReport.data = null
    })
    builder.addCase(fetchZadAccountingReport.rejected, (state, action) => {
      applyRejected(state.accountingReport, action)
      state.accountingReport.data = null
    })
    builder.addCase(fetchZadAccountingReport.fulfilled, (state, action) =>
      applyFulfilled(state.accountingReport, action.payload.report)
    )
  },
})

export default zadReportsSlice.reducer
export const actions = zadReportsSlice.actions
export {initialState as zadReportsInitialState}
